import {Dispatch, SetStateAction} from 'react'
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from '../../lib'

import {
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doMETHOD_ADD_TASK,
  doTEMP_TASK_DATA_REPLACE_TEMP_TASK,
  doMETHOD_UPDATE_QUESTIONNAIRE,
} from '../state'
import {getToken} from './token_fetcher'
import {
  createDisplayStrategyUpsertListMap,
  parseDisplayStrategyUpsertListFromMap,
  QuestionnaireTempSection,
  sanitizeTempSection,
  TempQuestionnaireDisplayStrategyUpsertItem,
} from '../../model'

export const REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE =
  'REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE'
export const doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE = createDoRequestAction(
  REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
)

export const requestTaskQuestionnaireWithDisplayStrategyCreateActionCreators = {
  doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
}

interface Payload {
  payload: {
    requestId: string
    name: string
    description?: string
    methodId: string
    scheduleRepeat: boolean
    scheduleDescription: string
    reminderList?: Record<string, string>[]
    color: string
    tempTaskId?: string
    sectionList: QuestionnaireTempSection[]
    displayStrategyList?: TempQuestionnaireDisplayStrategyUpsertItem[]
  }
  setRequestResult?: Dispatch<SetStateAction<RequestResult | null>>
}

export function* requestTaskQuestionnaireWithDisplayStrategyCreate({payload, setRequestResult}: Payload): SagaIterator {
  debug('saga*requestTaskQuestionnaireWithDisplayStrategyCreate')

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        methodId: v.string().uuid().exist(),
        name: v.string().exist(),
        description: v.string().optional(),
        scheduleRepeat: v.boolean().optional(),
        scheduleDescription: v.string().optional(),
        reminderList: v.array().optional(),
        sectionList: v.array(),
        displayStrategyList: v.array().optional(),
        color: v.string().exist(),
        tempTaskId: v.string().uuid().optional(),
      }),
    })
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    )
  }

  const {
    name,
    description,
    methodId,
    scheduleRepeat,
    scheduleDescription,
    reminderList,
    sectionList,
    displayStrategyList,
    color,
    tempTaskId,
  } = payload

  const accessToken = yield call(getToken)

  const createTaskResult = yield request({
    method: 'post',
    url: `${API_URL}/v1/web/task-questionnaire-create`,
    accessToken,
    data: {
      name,
      description: description || '',
      color,
      methodId,
      scheduleRepeat,
      scheduleDescription,
      reminderList,
      sectionList: sectionList.map(sanitizeTempSection),
    },
  })

  if (!createTaskResult.success || createTaskResult.error) {
    const error = createTaskResult.error || new Error('request did not succeed')
    debug(error)
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
          fromPayload: payload,
          requestId: payload.requestId,
          url: '/v1/web/task-questionnaire-create',
          ...createTaskResult,
          error,
        },
        setRequestResult,
      ),
    )
  }

  if (tempTaskId) {
    yield put(
      doTEMP_TASK_DATA_REPLACE_TEMP_TASK({
        tempTaskId,
        newId: createTaskResult.payload.id,
      }),
    )
  } else {
    yield put(doMETHOD_ADD_TASK({...createTaskResult.payload}))
  }

  // create display strategy if provided
  let displayStrategyResult = {}
  if (displayStrategyList?.length) {
    const displayStrategyUpsertItemMapList = createDisplayStrategyUpsertListMap(
      sectionList, //tempQuestionnaireSectionList
      displayStrategyList,
    )

    const newQuestionnaire = createTaskResult.payload.questionnaire
    const upsertDisplayStrategyResult = yield request({
      method: 'post',
      url: `${API_URL}/v1/web/questionnaire-display-strategy-upsert`,
      accessToken,
      data: {
        questionnaireId: newQuestionnaire.id,
        displayStrategyList: parseDisplayStrategyUpsertListFromMap(
          newQuestionnaire.sectionList,
          displayStrategyUpsertItemMapList,
        ),
      },
    })

    if (!upsertDisplayStrategyResult.success || upsertDisplayStrategyResult.error) {
      const error = upsertDisplayStrategyResult.error || new Error('request did not succeed')
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
            requestId: payload.requestId,
            error,
          },
          setRequestResult,
        ),
      )
    }

    yield put(
      doMETHOD_UPDATE_QUESTIONNAIRE({
        ...upsertDisplayStrategyResult.payload,
      }),
    )
    displayStrategyResult = {...upsertDisplayStrategyResult.payload}
  }

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
        requestId: payload.requestId,
        ...createTaskResult,
        upsertDisplayStrategyResult: displayStrategyResult,
      },
      setRequestResult,
    ),
  )
}

export function* taskQuestionnaireWithDisplayStrategyCreateSaga() {
  yield takeEvery(
    REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE as any,
    requestTaskQuestionnaireWithDisplayStrategyCreate,
  )
}
